import { useEffect,useState } from 'react';

import useIsFirstRender from '@/hooks/useIsFirstRender';
import useProtectAction from '@/hooks/useProtectAction';
import useStateHandler from '@/hooks/useStateHandler';
import UserService from '@/services/user.service';
import LikeIcon from '@/shared-components/icons/components/actions/LikeIcon';
import { isSuccessfulMutationResponse, optimisticMutationCoordinator } from '@/utils/optimisticMutation';

import "./styles/LikeButton.scss";

export default function LikeButton({ itemId, typeOfItem, isLiked, className="", onLikeAction=null, unregisteredIntent = null }) {
  const { cacheHandler, stateHandler } = useStateHandler();
  const [likeStatus, setLikeStatus] = useState(initLikeStatus());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const privateActionHandler = useProtectAction({ typeOfProtection: "USER_ACCOUNT_REQUIRED" });
  const isFirstRender = useIsFirstRender();

  useEffect(()=>{
    if(!isFirstRender) {
      setLikeStatus(initLikeStatus());
    }
  }, [isLiked]);

  function initLikeStatus() {
    const cachedAction = cacheHandler.getCacheValue(`user_actions.like.${typeOfItem}_${itemId}`);
    return cachedAction ? cachedAction.data : isLiked; 
  }

  function updateLikeStatus(nextLikeStatus) {
    cacheHandler.storeInCache(`user_actions.like.${typeOfItem}_${itemId}`, nextLikeStatus, { type: "@cache/no-expiration" });
    setLikeStatus(nextLikeStatus);
    onLikeAction?.(nextLikeStatus);
    cacheHandler.triggerAction(`LIKE_${typeOfItem}`);
  }

  async function handleLikeAction() {
    if (isSubmitting) return;

    const previousLikeStatus = likeStatus === true;
    const nextLikeStatus = !previousLikeStatus;
    const mutationKey = `user_actions.like.${typeOfItem}_${itemId}`;
    const mutationId = optimisticMutationCoordinator.begin(mutationKey);
    setIsSubmitting(true);
    updateLikeStatus(nextLikeStatus);

    try {
      const result = await (nextLikeStatus
        ? UserService.likeItem(typeOfItem, itemId)
        : UserService.unlikeItem(typeOfItem, itemId));
      if (!isSuccessfulMutationResponse(result)) {
        throw new Error("Unable to update like status");
      }
    } catch (_error) {
      if (optimisticMutationCoordinator.isCurrent(mutationKey, mutationId)) {
        updateLikeStatus(previousLikeStatus);
      }
    } finally {
      optimisticMutationCoordinator.complete(mutationKey, mutationId);
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <LikeIcon 
        className={`like-button__button ${className} ${likeStatus === true ? "active" : ""}`} 
        aria-busy={isSubmitting}
        onClick={(e)=>{
          e.stopPropagation();
          // Prefer explicit intent if provided by parent
          if (unregisteredIntent) {
            stateHandler.set('unregisteredUser', unregisteredIntent);
          } else {
            // Choose unregistered message based on context (className)
            const cls = String(className || '');
            if (
              cls.includes('article__cover-like-button') ||
              cls.includes('article__footer-like-button') ||
              cls.includes('boardcard__footer-like-button')
            ) {
              stateHandler.set('unregisteredUser', 'guest_like_article');
            } else if (cls.includes('post__footer-like-button')) {
              stateHandler.set('unregisteredUser', 'guest_like_post');
            } else if (
              cls.includes('artlist-options__artlist-like')
            ) {
              // Artlist like without username context: fall back to generic post-like
              stateHandler.set('unregisteredUser', 'guest_like_post');
            } else {
              stateHandler.set('unregisteredUser', 'guest_like_artwork');
            }
          }
          privateActionHandler(handleLikeAction);
        }}
      />
    </>
  );
}
